import { clozeStatus } from "./text.js";

/**
 * Client adapter: replaces the artifact-only `claude.use("sample")` call.
 *
 * In the prototype, runEnrich() does this:
 *     const sample = await window.claude.use("sample");
 *     const out = await sample.json(prompt);
 *
 * Neither `window.claude` nor `sample` exists outside the Claude artifact
 * viewer, so in the real app that whole path is replaced by enrichWords()
 * below. The prompt now lives in the Worker, not the client, so you can
 * improve it by redeploying the Worker, without shipping an app update.
 */

/* A bare host like "foo.workers.dev" (no scheme) is treated by fetch() as a
 * RELATIVE path, so the request silently goes to your own site instead of the
 * Worker and comes back 405. Normalise rather than trust the env var. */
function normaliseUrl(raw) {
  const v = String(raw || "").trim();
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v.replace(/\/+$/, "");
  return "https://" + v.replace(/^\/+/, "").replace(/\/+$/, "");
}

const ENRICH_URL = normaliseUrl(import.meta.env.VITE_ENRICH_URL);

/* Surfaced so the Settings screen can show what it's actually calling. */
export const enrichEndpoint = () => ENRICH_URL;

/** Is enrichment configured at all? Hide the UI when it isn't. */
export const enrichAvailable = () => Boolean(ENRICH_URL);

/**
 * @param {Array<{id:string, term:string, trans:string}>} cards  max 20
 * @returns {Promise<Array<{id, art, plural, pos, ex}>>}
 * @throws {{code:string, detail?:string}}
 */
export async function enrichWords(cards) {
  if (!ENRICH_URL) throw { code: "not_configured" };

  const words = cards.slice(0, 20).map((c) => ({
    id: c.id,
    word: c.term,
    meaning: c.trans,
  }));

  let res;
  try {
    res = await fetch(ENRICH_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ words }),
    });
  } catch (e) {
    /* fetch() throws the same TypeError for a blocked CORS request as for a
       dead connection, so distinguish them: if the browser is online, a throw
       here almost always means the Worker refused this origin. */
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      throw { code: "offline" };
    }
    throw { code: "blocked", detail: location.origin };
  }

  /* If the request landed on our own origin the env var is wrong, and the
     response will be the static host's, not the Worker's. Worth naming
     precisely, because the generic "try again" sent people in circles. */
  try {
    if (new URL(ENRICH_URL, location.href).origin === location.origin) {
      throw { code: "wrong_url", detail: ENRICH_URL };
    }
  } catch (e) {
    if (e && e.code) throw e;
  }

  if (res.status === 405) throw { code: "wrong_url", detail: ENRICH_URL };

  const data = await res.json().catch(() => null);
  /* The Worker now names its failure in the body; prefer that over the bare
     status, which was all "HTTP 502" ever told anyone. */
  if (!res.ok || (data && data.error)) {
    const code = data && data.error;
    if (code === "rate_limited" || res.status === 429) throw { code: "rate_limited" };
    if (code === "server_misconfigured") throw { code: "no_ai_binding" };
    if (code === "unparseable") throw { code: "unparseable", detail: data.detail };
    if (code === "ai_failed") throw { code: "ai_failed", detail: data.detail };
    throw { code: "server", detail: code || `HTTP ${res.status}` };
  }
  if (!data || !Array.isArray(data.results)) throw { code: "unparseable" };
  return data.results;
}

/**
 * Apply results to cards, returning a record of exactly what changed so the
 * UI can show its work. An example sentence is only accepted if it actually
 * contains the word; otherwise Lückentext can't use it, and a plausible but
 * unrelated sentence is worse than none.
 *
 * @returns {Array<{id,term,changes:Array<{field,from,to}>,rejected:string[]}>}
 */
export function applyEnrichment(state, results, opts = {}) {
  /* opts.only limits which fields may change, e.g. ["example"] when the user
     asked for new sentences and shouldn't have a hand-corrected article
     silently overwritten. */
  const allow = (f) => !opts.only || opts.only.includes(f);
  const log = [];
  for (const row of results) {
    const c = state.cards.find((x) => x.id === row.id);
    if (!c) continue;
    const changes = [], rejected = [];

    if (allow("type") && row.pos && row.pos !== c.pos) {
      changes.push({ field: "type", from: posName(c.pos), to: posName(row.pos) });
      c.pos = row.pos;
    }
    if (allow("article") && row.art && row.art !== c.art) {
      changes.push({ field: "article", from: c.art || "none", to: row.art });
      c.art = row.art;
    } else if (allow("article") && !row.art && c.pos !== "n" && c.art) {
      changes.push({ field: "article", from: c.art, to: "none" });
      c.art = null;
    }
    if (allow("plural") && row.plural && c.pos === "n" && row.plural !== c.plural) {
      changes.push({ field: "plural", from: c.plural || "none", to: row.plural });
      c.plural = row.plural;
    }
    if (allow("example") && row.ex) {
      const ex = row.ex.trim();
      /* The same rule the Lückentext game uses, so a sentence is never
         accepted here and then silently skipped there. */
      const st = clozeStatus({ term: c.term, pos: c.pos, ex });
      if (st.cz) {
        if (ex !== c.ex) {
          changes.push({ field: "example", from: c.ex || "none", to: ex });
          c.ex = ex;
          delete c.gap;   // a hand-picked gap word belonged to the old sentence
        }
      } else {
        rejected.push(st.reason === "short"
          ? "example sentence was too short around the word"
          : "Lückentext couldn't find the word in the sentence (open it under Your words to pick it by hand)");
      }
    }

    if (changes.length || rejected.length) {
      c.enrichedAt = Date.now();
      log.push({ id: c.id, term: c.term, changes, rejected });
    }
  }
  return log;
}

const posName = (p) => ({ n: "noun", v: "verb", a: "adjective" }[p] || p);

/* Viewer-facing copy per error code. */
export const enrichMessage = (code, detail) =>
  ({
    offline: "You're offline. Enrichment needs a connection.",
    blocked:
      "The Worker refused this site (" +
      (detail || "?") +
      "). Add it to ALLOWED_ORIGINS in the Worker's settings.",
    rate_limited: "Today's free AI allowance is used up. It resets tomorrow.",
    unparseable: "The AI's reply couldn't be read. Try fewer words at once.",
    ai_failed: "The AI service returned an error" + (detail ? `: ${detail}` : "") + ".",
    not_configured: "Enrichment isn't set up: VITE_ENRICH_URL is empty.",
    no_ai_binding: "The Worker is missing its Workers AI binding (name it AI).",
    wrong_url:
      "The request went to the wrong address: " +
      (detail || "?") +
      ". VITE_ENRICH_URL must start with https:// and point at the Worker.",
    server: "The Worker returned an error" + (detail ? ` (${detail})` : "") + ".",
  }[code] || "That didn't work. Try again in a moment.");
